import React, { useCallback, ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Card from '../../../../../components/card/card.component';
import PageTabs from '../../../../../components/page-tabs/page-tabs.component';
import SettingsUserAccountActivity from './settings-user-account-activity/settings-user-account-activity.component';
import SettingsUserSecuritySummary from './settings-user-security-summary/settings-user-security-summary.component';
import FormatService from '../../../../../services/format-service';
import { TAB_NAME_SUMMARY, TAB_NAME_ACCOUNT_ACTIVITY } from '../../../../../constants/system/tab-names';
import { QUERY_PARAM_TAB } from '../../../../../constants/system/query-params';
import { ROUTE_SETTINGS_SECURITY } from '../../../../../constants/system/routes';

const SettingsUserSecurity: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    isFormChanged,
    isCurrentUser,
    isUserApiKeyChanged,
    formError,
    userData,
    currentUser,
    otpSecretHash,
    otpSecretQrCodeLink,
    importExportErrorMessage,
    connectOtpModalErrorMessage,
    dealList,
    portfolioList,
    hiddenPortfolioList,
    accessLevelList,
    accountUserTypeList,
    verificationTypeList,
    onCancelForm,
    onSubmitForm,
    onDealSearch,
    onDealSearchClose,
    onBeforeLocationChange,
    onCloseUserSessions,
    onResetSettings,
    onExportSettings,
    onImportSettings,
    onGetUserApiKey,
    onCopyUserApiKey,
    onConnectOtpData,
    onVerifyOtpSecretData,
    onDeleteOtpSecretData,
    onGetOtpSecretData,
    onVerifyPhone,
    onAccountUserDealsChange,
    onAccountUserPortfoliosChange,
    onAccountUserTypeChange,
    onAccountUserDealAccessLevelChange,
    onImportSettingsFileDelete,
  } = props;

  const location: ReactRouterLocation = useLocation();
  const navigate: ReactRouterNavigate = useNavigate();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_SUMMARY,
      title: 'Summary',
      component: (
        <SettingsUserSecuritySummary
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isCurrentUser={isCurrentUser}
          isUserApiKeyChanged={isUserApiKeyChanged}
          currentUser={currentUser}
          formError={formError}
          userData={userData}
          otpSecretHash={otpSecretHash}
          otpSecretQrCodeLink={otpSecretQrCodeLink}
          importExportErrorMessage={importExportErrorMessage}
          connectOtpModalErrorMessage={connectOtpModalErrorMessage}
          dealList={dealList}
          portfolioList={portfolioList}
          hiddenPortfolioList={hiddenPortfolioList}
          accountUserTypeList={accountUserTypeList}
          accessLevelList={accessLevelList}
          verificationTypeList={verificationTypeList}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onCloseUserSessions={onCloseUserSessions}
          onResetSettings={onResetSettings}
          onExportSettings={onExportSettings}
          onImportSettings={onImportSettings}
          onImportSettingsFileDelete={onImportSettingsFileDelete}
          onAccountUserTypeChange={onAccountUserTypeChange}
          onAccountUserDealsChange={onAccountUserDealsChange}
          onAccountUserPortfoliosChange={onAccountUserPortfoliosChange}
          onAccountUserDealAccessLevelChange={onAccountUserDealAccessLevelChange}
          onDealSearch={onDealSearch}
          onDealSearchClose={onDealSearchClose}
          onGetUserApiKey={onGetUserApiKey}
          onCopyUserApiKey={onCopyUserApiKey}
          onConnectOtpData={onConnectOtpData}
          onVerifyOtpSecretData={onVerifyOtpSecretData}
          onDeleteOtpSecretData={onDeleteOtpSecretData}
          onGetOtpSecretData={onGetOtpSecretData}
          onVerifyPhone={onVerifyPhone}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_ACCOUNT_ACTIVITY,
      title: 'Activity',
      component: (
        <SettingsUserAccountActivity
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          userData={userData}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onCloseUserSessions={onCloseUserSessions}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: ROUTE_SETTINGS_SECURITY,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [locationQueryParams, tabs]);

  const tabItemSummary: TabItem | null = tabs && tabs.length ? tabs.find((tab: TabItem): boolean => (
    tab.name === TAB_NAME_SUMMARY
  )) || null : null;
  const tabItemSummaryComponent: ReactElement | null = tabItemSummary && tabItemSummary.component
    ? tabItemSummary.component : null;

  return (
    <Card isContainer isFullWidth withNoPadding>
      {
        isCurrentUser ? (
          <PageTabs
            isDisabled={isFormChanged}
            isCompressed={isRightDrawerOpen}
            tabs={tabs}
            currentTab={queryTab}
            onTabChange={onTabChange}
            onBeforeLocationChange={onBeforeLocationChange}
          />
        ) : (
          tabItemSummaryComponent
        )
      }
    </Card>
  );
});

export default SettingsUserSecurity;
